import React, { useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Search, 
  Filter, 
  BookOpen, 
  Clock, 
  Star,
  TrendingUp,
  X
} from 'lucide-react';
import { ALL_COURSE_CATEGORIES } from '@/config/courseNavigation';
import { useNavigate } from 'react-router-dom';

interface SearchFilters {
  level: string[];
  category: string[];
  duration: string[];
}

const CourseSearchInterface: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<SearchFilters>({
    level: [],
    category: [],
    duration: []
  });
  const [showFilters, setShowFilters] = useState(false);

  // Flatten all courses from all categories
  const allCourses = useMemo(() => {
    return ALL_COURSE_CATEGORIES.flatMap(category => 
      category.courses.map(course => ({
        ...course,
        categoryId: category.id,
        categoryTitle: category.title,
        categoryIcon: category.icon
      }))
    );
  }, []);

  // Filter courses based on search term and active filters
  const filteredCourses = useMemo(() => {
    let filtered = allCourses;

    // Text search
    if (searchTerm) {
      filtered = filtered.filter(course =>
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.categoryTitle.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Level filter
    if (activeFilters.level.length > 0) {
      filtered = filtered.filter(course =>
        activeFilters.level.includes(course.level)
      );
    }

    // Category filter
    if (activeFilters.category.length > 0) {
      filtered = filtered.filter(course =>
        activeFilters.category.includes(course.categoryId)
      );
    }

    // Duration filter (simplified)
    if (activeFilters.duration.length > 0) {
      filtered = filtered.filter(course => {
        const duration = course.duration.toLowerCase();
        return activeFilters.duration.some(filter => {
          switch (filter) {
            case 'short':
              return duration.includes('min') || duration.includes('1 hour');
            case 'medium':
              return duration.includes('2 hour') || duration.includes('3 hour');
            case 'long':
              return duration.includes('4 hour') || duration.includes('5+ hour');
            default:
              return false;
          }
        });
      });
    }

    return filtered;
  }, [allCourses, searchTerm, activeFilters]);

  // Get unique values for filters
  const availableLevels = [...new Set(allCourses.map(course => course.level))];
  const availableCategories = ALL_COURSE_CATEGORIES.map(cat => ({
    id: cat.id,
    title: cat.title
  }));

  const toggleFilter = (type: keyof SearchFilters, value: string) => {
    setActiveFilters(prev => ({
      ...prev,
      [type]: prev[type].includes(value)
        ? prev[type].filter(item => item !== value)
        : [...prev[type], value]
    }));
  };

  const clearAllFilters = () => {
    setActiveFilters({
      level: [],
      category: [],
      duration: []
    });
    setSearchTerm('');
  };

  const activeFilterCount = 
    activeFilters.level.length + 
    activeFilters.category.length + 
    activeFilters.duration.length;

  return (
    <div className="space-y-6">
      {/* Search Header */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search courses, categories, or skills..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="gap-2"
          >
            <Filter className="h-4 w-4" />
            Filters
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-1">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
          {activeFilterCount > 0 && (
            <Button variant="outline" onClick={clearAllFilters} className="gap-2">
              <X className="h-4 w-4" />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {filteredCourses.length} courses found
          {searchTerm && ` for "${searchTerm}"`}
        </p>
        <div className="flex gap-2">
          <Badge variant="outline" className="gap-1">
            <BookOpen className="h-3 w-3" />
            {ALL_COURSE_CATEGORIES.length} Categories
          </Badge>
          <Badge variant="outline" className="gap-1">
            <Star className="h-3 w-3" />
            {allCourses.length} Total Courses
          </Badge>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filter Courses</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="level" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="level">Level</TabsTrigger>
                <TabsTrigger value="category">Category</TabsTrigger>
                <TabsTrigger value="duration">Duration</TabsTrigger>
              </TabsList>

              <TabsContent value="level" className="mt-4">
                <div className="flex flex-wrap gap-2">
                  {availableLevels.map(level => (
                    <Badge
                      key={level}
                      variant={activeFilters.level.includes(level) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleFilter('level', level)}
                    >
                      {level}
                    </Badge>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="category" className="mt-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {availableCategories.map(category => (
                    <Badge
                      key={category.id}
                      variant={activeFilters.category.includes(category.id) ? 'default' : 'outline'}
                      className="cursor-pointer justify-start p-2"
                      onClick={() => toggleFilter('category', category.id)}
                    >
                      {category.title}
                    </Badge>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="duration" className="mt-4">
                <div className="flex flex-wrap gap-2">
                  {['short', 'medium', 'long'].map(duration => (
                    <Badge
                      key={duration}
                      variant={activeFilters.duration.includes(duration) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleFilter('duration', duration)}
                    >
                      {duration === 'short' && '< 2 hours'}
                      {duration === 'medium' && '2-4 hours'}
                      {duration === 'long' && '4+ hours'}
                    </Badge>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Search Results */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredCourses.map((course) => {
          const CategoryIcon = course.categoryIcon;
          return (
            <Card 
              key={`${course.categoryId}-${course.id}`}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(course.route)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <CategoryIcon className="h-5 w-5 text-primary" />
                    <Badge variant="outline" className="text-xs">
                      {course.categoryTitle}
                    </Badge>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {course.level}
                  </Badge>
                </div>
                <CardTitle className="text-lg leading-tight">
                  {course.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {course.duration}
                  </div>
                  <div className="flex items-center gap-1">
                    <TrendingUp className="h-4 w-4" />
                    Popular
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* No Results */}
      {filteredCourses.length === 0 && (
        <Card className="text-center py-12">
          <CardContent>
            <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No courses found</h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your search terms or filters
            </p>
            <Button onClick={clearAllFilters} variant="outline">
              Clear all filters
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CourseSearchInterface;